// Layer 2 (Engine): NSR, recovery-shortfall cost and operating-cost reads —
// pure functions over the smelter schedule, readings and session state.

import type { SessionState } from '@/versions/v1/schema/scenario';
import {
  costCentreMtdActual,
  costCentreMtdBudget,
} from '@/versions/v1/shared/bunker-hill-cost-centres';
import { BUNKER_HILL_SMELTER_SCHEDULE } from '@/versions/v1/shared/bunker-hill-smelter-terms';
import { readingByIdLookup } from '@/versions/v1/shared/reading.derive';
import { valueAt } from '@/versions/v1/shared/series.derive';
import { shortTonsToTonnes } from './unit-conversion';
import { smelterSettlement, type SmelterSettlementInput } from './SmelterSettlementEngine';
import { formatCurrency, formatCurrencyExact, formatCurrencyStandard } from './money';

export const NSR_SYN_PROVENANCE = BUNKER_HILL_SMELTER_SCHEDULE.provenance;

type ProductNsrBreakdown = {
  product: 'Pb' | 'Zn' | 'Ag';
  payableMetal: number;
  payableUnit: string;
  metalRevenue: number;
  treatmentCharge: number;
  freight: number;
  penalties: number;
  netValue: number;
  concentrateDmt: number;
};

export function readingNum(id: string, state: SessionState, field: 'value' | 'design' = 'value'): number {
  const base = readingByIdLookup(id);
  if (!base) throw new Error(`Missing reading ${id}`);
  if (field === 'design') {
    const n = base.design ?? base.value;
    if (n === undefined) throw new Error(`Missing design on ${id}`);
    return n;
  }
  const n = valueAt(id, state) ?? base.value ?? base.design;
  if (n === undefined) throw new Error(`Missing value on ${id}`);
  return n;
}

export function elapsedProductionDays(state: SessionState): number {
  const day = Number.parseInt(state.cursor.split('T')[0]!.split('-')[2]!, 10);
  return day > 0 ? day : 1;
}

type MetalKey = 'pb' | 'zn' | 'ag';

/** Shared price-and-terms path for callers with short-ton feed and plain plant readings. */
export function settleAtShortTonFeed(
  input: Omit<SmelterSettlementInput, 'feedMetricTonnesPerDay'>,
) {
  return smelterSettlement({
    ...input,
    feedMetricTonnesPerDay: shortTonsToTonnes(input.feedShortTonsPerDay),
  });
}

function readingsForSettlement(state: SessionState, recoveries: NsrRecoveries, feedShortTonsPerDay?: number) {
  return settleAtShortTonFeed({
    feedShortTonsPerDay: feedShortTonsPerDay ?? readingNum('rd-kpi-ore-td', state),
    headGrades: {
      leadFraction: readingNum('rd-head-pb', state) / 100,
      zincFraction: readingNum('rd-head-zn', state) / 100,
      silverOzPerShortTon: readingNum('rd-head-ag', state),
    },
    recoveries: {
      leadPct: recoveries.pb,
      zincPct: recoveries.zn,
      silverPct: recoveries.ag,
    },
    concentrateGrades: {
      leadPct: readingNum('rd-kpi-pb-grade', state),
      zincPct: readingNum('rd-kpi-zn-grade', state),
    },
    assays: {
      leadAsPct: readingNum('rd-spec-pb-as', state),
      leadHgPpm: readingNum('rd-spec-pb-hg', state),
      zincAsPct: readingNum('rd-spec-zn-as', state),
      zincPbPct: readingNum('rd-spec-zn-pb', state),
    },
  });
}

function computeNsrDaily(
  state: SessionState,
  recoveries: { pb: number; zn: number; ag: number },
  feedShortTonsPerDay?: number,
): {
  pb: ProductNsrBreakdown;
  zn: ProductNsrBreakdown;
  ag: ProductNsrBreakdown;
  total: number;
  payableMetal: number;
  treatmentCharge: number;
  penalties: number;
  freight: number;
} {
  const settlement = readingsForSettlement(state, recoveries, feedShortTonsPerDay);
  const asProduct = (product: 'Pb' | 'Zn' | 'Ag', read: typeof settlement.lead | typeof settlement.zinc | typeof settlement.silver, hostDmt = read.concentrateDmtPerDay): ProductNsrBreakdown => ({
    product,
    payableMetal: read.payableMetalPerDay,
    payableUnit: product === 'Ag' ? 'oz' : 't',
    metalRevenue: read.grossRevenueA,
    treatmentCharge: read.treatmentChargeA,
    freight: read.transportA,
    penalties: read.penaltiesA,
    netValue: read.netNsrA,
    concentrateDmt: hostDmt,
  });
  const pbActual = asProduct('Pb', settlement.lead);
  const znActual = asProduct('Zn', settlement.zinc);
  const agActual = asProduct('Ag', settlement.silver, settlement.lead.concentrateDmtPerDay);

  return {
    pb: pbActual,
    zn: znActual,
    ag: agActual,
    total: settlement.totalNsrA,
    payableMetal: pbActual.metalRevenue + znActual.metalRevenue + agActual.metalRevenue,
    treatmentCharge: settlement.treatmentChargesA,
    penalties: settlement.penaltiesA,
    freight: settlement.transportA,
  };
}

export type NsrRead = {
  daily: {
    pb: ProductNsrBreakdown;
    zn: ProductNsrBreakdown;
    ag: ProductNsrBreakdown;
    total: number;
    payableMetal: number;
    treatmentCharge: number;
    penalties: number;
    freight: number;
  };
  mtd: number;
  budgetDaily: number;
  basisLabel: string;
  provenance: string;
};

export type NsrRecoveries = { pb: number; zn: number; ag: number };

export type NsrRecoveryOverrides = {
  actual?: NsrRecoveries;
  budget?: NsrRecoveries;
};

export function nsrRead(
  state: SessionState,
  feedShortTonsPerDay?: number,
  recoveryOverrides: NsrRecoveryOverrides = {},
): NsrRead {
  const actual = recoveryOverrides.actual ?? {
    pb: readingNum('rd-kpi-pb-rec', state),
    zn: readingNum('rd-kpi-zn-rec', state),
    ag: readingNum('rd-kpi-ag-rec', state), // K11c: Ag's own measured reading.
  };
  const budget = recoveryOverrides.budget ?? {
    pb: readingNum('rd-kpi-pb-rec', state, 'design'),
    zn: readingNum('rd-kpi-zn-rec', state, 'design'),
    ag: readingNum('rd-kpi-pb-rec', state, 'design'),
  };

  const actualDaily = computeNsrDaily(state, actual, feedShortTonsPerDay);
  const budgetDaily = computeNsrDaily(state, budget, feedShortTonsPerDay).total;

  const mtd = actualDaily.total * elapsedProductionDays(state);

  return {
    daily: actualDaily,
    mtd,
    budgetDaily,
    basisLabel: 'Budget',
    provenance: NSR_SYN_PROVENANCE,
  };
}

export type ShortfallMetal = {
  metal: 'Pb' | 'Zn' | 'Ag';
  dailyCost: number;
  mtdCost: number;
};

export type RecoveryShortfallCost = {
  metals: readonly ShortfallMetal[];
  dailyTotal: number;
  mtdTotal: number;
  terms: string;
  basisLabel: string;
  provenance: string;
};

function shortfallForMetal(metal: MetalKey, state: SessionState): ShortfallMetal {
  const actual = {
    pb: readingNum('rd-kpi-pb-rec', state),
    zn: readingNum('rd-kpi-zn-rec', state),
    ag: readingNum('rd-kpi-ag-rec', state), // K11c: Ag's own measured reading.
  };
  const budget = {
    pb: readingNum('rd-kpi-pb-rec', state, 'design'),
    zn: readingNum('rd-kpi-zn-rec', state, 'design'),
    ag: readingNum('rd-kpi-pb-rec', state, 'design'),
  };
  const budgetDaily = computeNsrDaily(state, budget).total;

  const recoveries = {
    pb: metal === 'pb' ? actual.pb : budget.pb,
    zn: metal === 'zn' ? actual.zn : budget.zn,
    ag: metal === 'ag' ? actual.ag : budget.ag,
  };
  const partialDaily = computeNsrDaily(state, recoveries).total;
  const dailyCost = budgetDaily - partialDaily;

  return {
    metal: metal === 'pb' ? 'Pb' : metal === 'zn' ? 'Zn' : 'Ag',
    dailyCost,
    mtdCost: dailyCost * elapsedProductionDays(state),
  };
}

export function recoveryShortfallCost(state: SessionState): RecoveryShortfallCost {
  const nsr = nsrRead(state);
  const dailyTotal = nsr.budgetDaily - nsr.daily.total;
  const mtdTotal = dailyTotal * elapsedProductionDays(state);
  const metals = [
    shortfallForMetal('pb', state),
    shortfallForMetal('zn', state),
    shortfallForMetal('ag', state),
  ];
  const schedule = BUNKER_HILL_SMELTER_SCHEDULE;
  const terms = `Pb ${schedule.products.find((product) => product.product === 'Pb')!.payable.payableFraction * 100}% payable · Zn ${schedule.products.find((product) => product.product === 'Zn')!.payable.payableFraction * 100}% less ${schedule.products.find((product) => product.product === 'Zn')!.payable.gradeDeduction} unit · Ag ${schedule.silverOnLead.payableFraction * 100}% less ${schedule.silverOnLead.flatDeductionGt} g/t · TC · penalties · freight · prices ${schedule.prices.priceDate}`;

  return {
    metals,
    dailyTotal,
    mtdTotal,
    terms,
    basisLabel: 'Budget',
    provenance: NSR_SYN_PROVENANCE,
  };
}

export type OperatingCostRead = {
  actualPerT: number;
  targetPerT: number;
  deltaPct: number;
  mtdActual: number;
  mtdBudget: number;
  provenance: string;
};

export function operatingCostRead(state: SessionState): OperatingCostRead {
  const throughput = readingNum('rd-kpi-ore-td', state);
  const elapsed = elapsedProductionDays(state);
  const mtdActual = costCentreMtdActual();
  const mtdBudget = costCentreMtdBudget();
  const milledTonnes = throughput * elapsed;
  const actualPerT = mtdActual / milledTonnes;
  const targetPerT = mtdBudget / milledTonnes;
  const deltaPct = ((actualPerT - targetPerT) / targetPerT) * 100;

  return {
    actualPerT,
    targetPerT,
    deltaPct,
    mtdActual,
    mtdBudget,
    provenance: NSR_SYN_PROVENANCE,
  };
}

export function formatMoneyK(n: number): string {
  if (Math.abs(n) < 1_000) return formatCurrency(n);
  return formatCurrencyStandard(n);
}

export function formatMoney(n: number): string {
  return formatCurrencyStandard(n);
}

/** Shortfall metal rows share one dollar unit — all K or all whole dollars. */
export function formatMoneyConsistent(n: number): string {
  if (Math.abs(n) >= 10_000) return formatMoneyK(n);
  return formatCurrency(n);
}

export function formatMoneyPerT(n: number): string {
  return `${formatCurrencyExact(n)}/t`;
}

/** The June ore-tonnage unit: dry short tons. A per-tonne figure follows its
 *  tonnage, so the cost route's rate cards read `US$/dst`, never a bare `US$/t`. */
export function formatMoneyPerDst(n: number): string {
  return `${formatCurrencyExact(n)}/dst`;
}

export function formatSignedPct(n: number): string {
  const sign = n >= 0 ? '+' : '−';
  return `${sign}${Math.abs(n).toFixed(1)}%`;
}

export type RecoveryPayableLever = {
  metal: 'Pb' | 'Zn' | 'Ag';
  gapPt: number;
  leverPerPt: number;
  moneyPerPtA: number;
  unit: 't/d' | 'oz/d';
};

/** Marginal payable metal per +1 recovery point — head grade, recovery and smelter schedule via computeNsrDaily. */
export function recoveryPayableLevers(state: SessionState): RecoveryPayableLever[] {
  const baseRecoveries = {
    pb: readingNum('rd-kpi-pb-rec', state),
    zn: readingNum('rd-kpi-zn-rec', state),
    ag: readingNum('rd-kpi-ag-rec', state), // K11c: Ag's own measured reading.
  };
  const baseDaily = computeNsrDaily(state, baseRecoveries);
  const specs: Array<{
    key: 'pb' | 'zn' | 'ag';
    metal: 'Pb' | 'Zn' | 'Ag';
    readingId: string;
    unit: 't/d' | 'oz/d';
  }> = [
    { key: 'pb', metal: 'Pb', readingId: 'rd-kpi-pb-rec', unit: 't/d' },
    { key: 'zn', metal: 'Zn', readingId: 'rd-kpi-zn-rec', unit: 't/d' },
    { key: 'ag', metal: 'Ag', readingId: 'rd-kpi-ag-rec', unit: 'oz/d' },
  ];

  return specs.map(({ key, metal, readingId, unit }) => {
    const design = readingNum(readingId, state, 'design');
    const actualRec = baseRecoveries[key];
    const gapPt = actualRec - design;
    const bumped = { ...baseRecoveries, [key]: actualRec + 1 };
    const bumpedDaily = computeNsrDaily(state, bumped);
    const leverPerPt = bumpedDaily[key].payableMetal - baseDaily[key].payableMetal;
    return {
      metal,
      gapPt,
      leverPerPt: Math.round(leverPerPt * 10) / 10,
      moneyPerPtA: bumpedDaily.total - baseDaily.total,
      unit,
    };
  });
}
